import { postCloudAi, deleteCloudJournalEntry } from './cloudApi';

const ISO_DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** Push one saved journal day to Firestore (and RAG chunk index on the server). */
export async function syncJournalEntryToCloud(date, entryPayload) {
  const entryId = String(date || '').trim();
  if (!entryId) return false;
  try {
    const { getCurrentFirebaseIdToken } = await import('./firebase');
    const token = await getCurrentFirebaseIdToken();
    if (!token) return false;

    await postCloudAi('/api/journal/entries', {
      entry: {
        id: entryId,
        date: entryId,
        ...entryPayload,
      },
    });
    return true;
  } catch (e) {
    console.warn('[journalCloudSync] upload failed', e);
    return false;
  }
}

/**
 * After bulk local writes (e.g. JSON restore), push every affected day so Firestore + journal RAG stay in sync.
 * @param {Record<string, object>} entriesByDate map of YYYY-MM-DD → entry payload (same shape as saveEntry merge)
 * @param {{ batchSize?: number }} [opts]
 * @returns {Promise<{ synced: number; failed: number; skipped: boolean }>}
 */
export async function syncJournalEntriesToCloudBulk(entriesByDate, { batchSize = 5 } = {}) {
  const list = Object.keys(entriesByDate || {}).filter((d) => ISO_DATE_KEY.test(String(d).trim()));
  if (!list.length) return { synced: 0, failed: 0, skipped: false };

  const { getCurrentFirebaseIdToken } = await import('./firebase');
  const token = await getCurrentFirebaseIdToken();
  if (!token) return { synced: 0, failed: list.length, skipped: true };

  let synced = 0;
  let failed = 0;

  for (let i = 0; i < list.length; i += batchSize) {
    const chunk = list.slice(i, i + batchSize);
    const results = await Promise.all(
      chunk.map(async (date) => {
        const entry = entriesByDate[date];
        if (!entry || typeof entry !== 'object') return false;
        return syncJournalEntryToCloud(date, entry);
      }),
    );
    for (const ok of results) {
      if (ok) synced += 1;
      else failed += 1;
    }
  }

  return { synced, failed, skipped: false };
}

/** Remove cloud copy + embeddings when a local entry is deleted. */
export async function deleteJournalEntryFromCloud(date) {
  const entryId = String(date || '').trim();
  if (!entryId) return;
  try {
    const { getCurrentFirebaseIdToken } = await import('./firebase');
    const token = await getCurrentFirebaseIdToken();
    if (!token) return;
    await deleteCloudJournalEntry(entryId);
  } catch (e) {
    console.warn('[journalCloudSync] delete failed', e);
  }
}
